import type {
  ObraStatus,
  PainelActivity,
  PainelAlerts,
  PainelAttentionRow,
  PainelExpiration,
  PainelExpirationBand,
  PainelFiscalRow,
  PainelMonth,
  PainelReason,
  PainelSecretariaRow,
  PainelTotals,
  VisitStatus,
} from '@obras/shared';
import {
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyDelta,
  formatDays,
  formatPercent,
  formatRelativeDate,
  OBRA_STATUS_LABELS,
  PAINEL_EXPIRATION_LABELS,
  ratio,
  REPORT_TYPE_META,
  VISIT_STATUS_LABELS,
} from '@obras/shared';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  CalendarClock,
  CalendarOff,
  CalendarPlus,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleSlash,
  ClipboardCheck,
  Clock,
  FileText,
  HardHat,
  Minus,
  PauseCircle,
  Plus,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  UserX,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { BarList, type BarRow } from '../../components/charts/BarList.js';
import { Columns, type ColumnItem } from '../../components/charts/Columns.js';
import { ChartFrame, ChartTable, rotuloMes } from '../../components/charts/shared.js';
import { Kpi } from '../../components/ui/Card.js';
import { Avatar, EmptyState } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';
import { obrasSearch, type ObrasLink, type ObrasSearch } from '../obras/search.js';

const ICON = { strokeWidth: 1.5 } as const;

/** `1 obra` / `3 obras`. */
function plural(quantidade: number, singular: string, plural: string): string {
  return `${quantidade.toLocaleString('pt-BR')} ${quantidade === 1 ? singular : plural}`;
}

/**
 * O "nada a mostrar" de um cartão do painel.
 *
 * Uma linha, não o estado vazio de página inteira: aqui o vazio é quase sempre
 * notícia boa — nenhuma pendência, nenhuma obra parada — e não pede ação.
 */
function Quiet({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <p className="flex items-center gap-2.5 px-5 pb-5 text-[13px] text-muted-foreground">
      <Icon {...ICON} size={16} className="shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

/** Atalho no cabeçalho do cartão para a tela que detalha o assunto. */
export function CardLink({
  to,
  search,
  children,
}: {
  to: '/obras' | '/relatorios' | '/tarefas';
  /** Só a lista de obras aceita recorte pela URL. */
  search?: ObrasSearch | undefined;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      search={search ?? {}}
      className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-primary-strong transition-colors hover:text-primary"
    >
      {children}
      <ArrowRight {...ICON} size={14} aria-hidden />
    </Link>
  );
}

/** Rótulo de bloco dentro de um cartão: o versalete do indicador. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-3 text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
      {children}
    </h3>
  );
}

// ─────────────────────────────────────────────────────────── posição

export function KpiRow({ totals }: { totals: PainelTotals }) {
  const { byStatus } = totals;

  const partes = [
    byStatus.paralisada > 0 ? plural(byStatus.paralisada, 'paralisada', 'paralisadas') : null,
    byStatus.concluida > 0 ? plural(byStatus.concluida, 'concluída', 'concluídas') : null,
  ].filter((parte): parte is string => parte !== null);

  const noAcervo = plural(totals.obras, 'obra no acervo', 'obras no acervo');

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Kpi
        label="Obras em andamento"
        value={String(byStatus.em_andamento)}
        hint={partes.length > 0 ? partes.join(' · ') : noAcervo}
        link={{ to: '/obras', search: obrasSearch({ statuses: ['em_andamento'] }) }}
      />
      <Kpi
        label="Valor total de contratos"
        value={formatCurrency(totals.contractedCents)}
        hint={noAcervo}
      />
      <Kpi
        label="Pago"
        value={formatCurrency(totals.paidCents)}
        tone="success"
        hint={`${formatPercent(totals.executionRatio)} do contratado`}
      />
      <Kpi
        label="Saldo a executar"
        value={formatCurrency(totals.remainingCents)}
        hint={`${formatCurrencyCompact(totals.aliveRemainingCents)} em obras em curso`}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────── atenção

type AlertTone = 'danger' | 'warning' | 'paused';

const TONE_TEXT: Record<AlertTone, string> = {
  danger: 'text-danger',
  warning: 'text-warning',
  paused: 'text-paused',
};

/**
 * Os sete contadores, sempre todos — inclusive zerados.
 *
 * Sumir com "paralisadas" quando chega a zero tiraria justamente a informação
 * boa. O zero fica em cinza; o que é maior que zero ganha a cor do estado, e
 * só então o ícone colore.
 */
const ALERT_TILES: readonly {
  readonly key: keyof PainelAlerts;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly tone: AlertTone;
  /** O recorte da lista de obras que conta exatamente o que o contador diz. */
  readonly recorte: Parameters<typeof obrasSearch>[0];
}[] = [
  {
    key: 'overdueVisits',
    label: 'Visitas vencidas',
    icon: TriangleAlert,
    tone: 'danger',
    recorte: { pendencias: ['visita_vencida'] },
  },
  {
    key: 'dueSoonVisits',
    label: 'Visitas vencendo',
    icon: Clock,
    tone: 'warning',
    recorte: { pendencias: ['visita_vencendo'] },
  },
  {
    key: 'withoutVisit',
    label: 'Sem visita',
    icon: CalendarOff,
    tone: 'warning',
    recorte: { pendencias: ['sem_visita'] },
  },
  {
    key: 'paused',
    label: 'Paralisadas',
    icon: PauseCircle,
    tone: 'paused',
    recorte: { statuses: ['paralisada'] },
  },
  {
    key: 'expiredContracts',
    label: 'Contratos vencidos',
    icon: CalendarClock,
    tone: 'danger',
    recorte: { pendencias: ['contrato_vencido'] },
  },
  {
    key: 'behindSchedule',
    label: 'Execução atrasada',
    icon: TrendingDown,
    tone: 'warning',
    recorte: { pendencias: ['atrasada'] },
  },
  {
    key: 'withoutFiscal',
    label: 'Sem fiscal',
    icon: UserX,
    tone: 'warning',
    recorte: { pendencias: ['sem_fiscal'] },
  },
];

/**
 * Cada contador é um atalho para a lista de obras com o mesmo recorte.
 *
 * `fiscalId` é a carteira: no painel do fiscal os contadores somam só as obras
 * dele, e a lista precisa abrir com o mesmo recorte, senão o número clicado e
 * o total da lista não batem.
 */
export function AlertStrip({
  alerts,
  fiscalId,
}: {
  alerts: PainelAlerts;
  fiscalId: string | null;
}) {
  return (
    <ul className="grid grid-cols-2 gap-x-4 gap-y-4 px-5 pb-5 sm:grid-cols-4 lg:grid-cols-7">
      {ALERT_TILES.map((tile) => {
        const value = alerts[tile.key];
        const ativo = value > 0;

        return (
          <li key={tile.key}>
            <Link
              to="/obras"
              search={obrasSearch({
                ...tile.recorte,
                ...(fiscalId ? { fiscalIds: [fiscalId] } : {}),
              })}
              title="Ver na lista de obras"
              className="-m-1.5 flex items-start gap-2.5 rounded-sm p-1.5 transition-colors duration-100 hover:bg-muted"
            >
              <tile.icon
                size={16}
                strokeWidth={1.75}
                className={cn(
                  'mt-0.5 shrink-0',
                  ativo ? TONE_TEXT[tile.tone] : 'text-muted-foreground/50',
                )}
                aria-hidden
              />
              <span>
                <span
                  className={cn(
                    'numeral block text-[22px] leading-none',
                    ativo ? TONE_TEXT[tile.tone] : 'text-muted-foreground',
                  )}
                >
                  {value}
                </span>
                <span className="mt-1 block text-[12px] text-muted-foreground">{tile.label}</span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * O motivo é texto com um ícone na cor do estado, não uma pílula colorida.
 *
 * Três pílulas por obra, vezes oito obras, era uma parede de cor: o olho não
 * sabia por onde começar. Em texto corrido, a cor fica só no ícone, e a linha
 * se lê como frase — "visita vencida há 33 dias · contrato vencido há 12 dias".
 */
const REASON_STYLE: Record<PainelReason, { tone: string; icon: LucideIcon }> = {
  visita_vencida: { tone: 'text-danger', icon: TriangleAlert },
  paralisada: { tone: 'text-paused', icon: PauseCircle },
  contrato_vencido: { tone: 'text-danger', icon: CalendarClock },
  atrasada: { tone: 'text-warning', icon: TrendingDown },
  sem_fiscal: { tone: 'text-warning', icon: UserX },
  sem_visita: { tone: 'text-muted-foreground', icon: CalendarOff },
  visita_vencendo: { tone: 'text-warning', icon: Clock },
};

/**
 * O motivo com o número que o torna urgente.
 *
 * "Há 12 dias" e "pago 48% com 88% do prazo" — nunca uma diferença em pontos
 * percentuais: "atraso de 40 p.p." obrigava a saber o que é um ponto
 * percentual para entender que a obra pagou menos da metade com o prazo quase
 * no fim.
 */
function reasonLabel(row: PainelAttentionRow, reason: PainelReason): string {
  switch (reason) {
    case 'visita_vencida':
      return row.visitOverdueDays
        ? `Visita vencida há ${formatDays(row.visitOverdueDays)}`
        : 'Visita vencida';
    case 'paralisada':
      return row.pausedDays ? `Paralisada há ${formatDays(row.pausedDays)}` : 'Paralisada';
    case 'contrato_vencido':
      return row.contractExpiredDays
        ? `Contrato vencido há ${formatDays(row.contractExpiredDays)}`
        : 'Contrato vencido';
    case 'atrasada':
      return `Pago ${formatPercent(row.executionProgress, 0)} com ${formatPercent(row.timeProgress, 0)} do prazo`;
    case 'sem_fiscal':
      return 'Sem fiscal';
    case 'sem_visita':
      return 'Sem visita';
    case 'visita_vencendo':
      return 'Visita vencendo';
  }
}

interface FiscalGroup {
  readonly key: string;
  readonly name: string;
  readonly fiscal: PainelFiscalRow | null;
  readonly rows: PainelAttentionRow[];
}

/**
 * As pendências agrupadas por quem responde por elas.
 *
 * Todo fiscal com obra em curso aparece, com a carga dele na linha de cima e,
 * quando aberto, as obras que pedem ação. Quem está em dia aparece em dia. As
 * obras sem responsável formam o último grupo: a pendência ali é designar
 * alguém.
 */
function groupByFiscal(
  attention: readonly PainelAttentionRow[],
  fiscais: readonly PainelFiscalRow[],
): FiscalGroup[] {
  const groups = new Map<string, FiscalGroup>(
    fiscais.map((fiscal) => [fiscal.id, { key: fiscal.id, name: fiscal.name, fiscal, rows: [] }]),
  );

  for (const row of attention) {
    const key = row.fiscalId ?? 'sem-fiscal';
    const group = groups.get(key) ?? {
      key,
      name: row.fiscalId ? (row.fiscalName ?? '') : 'Sem fiscal responsável',
      fiscal: null,
      rows: [],
    };
    group.rows.push(row);
    groups.set(key, group);
  }

  const pendencias = (group: FiscalGroup): number =>
    group.rows.reduce((sum, row) => sum + row.reasons.length, 0);

  return [...groups.values()].sort(
    (a, b) =>
      Number(a.key === 'sem-fiscal') - Number(b.key === 'sem-fiscal') ||
      pendencias(b) - pendencias(a) ||
      (b.fiscal?.obras ?? 0) - (a.fiscal?.obras ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

type TileTone = 'muted' | 'warning' | 'danger' | 'success';

const TILE_TONE: Record<TileTone, string> = {
  muted: 'bg-muted text-muted-foreground',
  warning: 'bg-warning-bg text-warning',
  danger: 'bg-danger-bg text-danger',
  success: 'bg-success-bg text-success',
};

/**
 * Quadro de resumo na linha do fiscal: o número e o que ele conta.
 *
 * Quadrado, não pílula — `radius-xs` — e com o fundo tênue do estado. O
 * neutro deixa o número em `foreground` para ser lido antes do rótulo.
 */
function Tile({
  value,
  label,
  tone = 'muted',
}: {
  value?: number | undefined;
  label: string;
  tone?: TileTone | undefined;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1 rounded-xs px-2 py-1 text-[12px] whitespace-nowrap',
        TILE_TONE[tone],
      )}
    >
      {value === undefined ? null : (
        <span className={cn('tabular font-medium', tone === 'muted' && 'text-foreground')}>
          {value}
        </span>
      )}
      {label}
    </span>
  );
}

/** O resumo da linha recolhida: obras, pendências, visitas vencidas e última visita. */
function GroupTiles({ group }: { group: FiscalGroup }) {
  const pending = group.rows.length;
  const obras = group.fiscal?.obras ?? pending;

  return (
    <>
      <Tile value={obras} label={obras === 1 ? 'obra em curso' : 'obras em curso'} />
      {pending > 0 ? (
        <Tile value={pending} label="com pendência" tone="warning" />
      ) : (
        <Tile label="Sem pendência" tone="success" />
      )}
      {group.fiscal && group.fiscal.overdueVisits > 0 ? (
        <Tile
          value={group.fiscal.overdueVisits}
          label={group.fiscal.overdueVisits === 1 ? 'visita vencida' : 'visitas vencidas'}
          tone="danger"
        />
      ) : null}
      {group.fiscal ? (
        <Tile
          label={
            group.fiscal.lastVisitAt
              ? `última visita ${formatRelativeDate(group.fiscal.lastVisitAt)}`
              : 'sem visita registrada'
          }
        />
      ) : null}
    </>
  );
}

/**
 * A foto da obra, em miniatura, antes do nome.
 *
 * É a mesma capa do cartão de obras, para a pessoa reconhecer a obra de
 * relance, sem ler. Sem foto, o quadro fica no cinza da superfície com o
 * ícone da obra — nunca vazio, senão a lista fica com buracos.
 */
function ObraThumb({ url }: { url: string | null }) {
  return (
    <span className="grid h-10 w-14 shrink-0 place-items-center overflow-hidden rounded-sm bg-muted">
      {url ? (
        <img src={url} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <HardHat {...ICON} size={16} className="text-muted-foreground" aria-hidden />
      )}
    </span>
  );
}

function ObraRow({ row }: { row: PainelAttentionRow }) {
  return (
    <li>
      <Link
        to="/obras/$obraId"
        params={{ obraId: row.id }}
        className="flex items-center gap-3 rounded-md py-2 pr-3 pl-[42px] transition-colors hover:bg-muted"
      >
        <ObraThumb url={row.coverUrl} />

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="truncate text-[13px] font-medium">{row.name}</span>
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
              {row.contractNumber ?? 'sem contrato'}
            </span>
          </span>

          <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-muted-foreground">
            {row.reasons.map((reason) => {
              const style = REASON_STYLE[reason];
              return (
                <span key={reason} className="flex items-center gap-1">
                  <style.icon size={13} strokeWidth={1.75} className={style.tone} aria-hidden />
                  {reasonLabel(row, reason)}
                </span>
              );
            })}
          </span>
        </span>
      </Link>
    </li>
  );
}

/**
 * Uma linha por fiscal, recolhida: a linha inteira abre e fecha.
 *
 * Recolhida, a linha já responde "quem está como": obras, pendências, visitas
 * vencidas, última visita. Aberta, lista as obras que pedem ação. Começa
 * fechada no acervo — oito fiscais abertos de uma vez era a parede que se
 * pediu para derrubar — e aberta na carteira, onde a lista é da própria pessoa.
 */
export function AttentionByFiscal({
  attention,
  fiscais,
  defaultOpen,
}: {
  attention: readonly PainelAttentionRow[];
  fiscais: readonly PainelFiscalRow[];
  defaultOpen: boolean;
}) {
  const groups = groupByFiscal(attention, fiscais);
  const [open, setOpen] = useState<ReadonlySet<string>>(
    () => new Set(defaultOpen ? groups.map((group) => group.key) : []),
  );

  function toggle(key: string): void {
    setOpen((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(key)) proximo.delete(key);
      else proximo.add(key);
      return proximo;
    });
  }

  if (groups.length === 0) return <Quiet icon={HardHat}>Nenhuma obra em curso.</Quiet>;

  return (
    <ul className="divide-y divide-border px-2 pb-2">
      {groups.map((group) => {
        const aberto = open.has(group.key);
        const painelId = `atencao-${group.key}`;

        return (
          <li key={group.key} className="py-1">
            <button
              type="button"
              aria-expanded={aberto}
              aria-controls={painelId}
              onClick={() => toggle(group.key)}
              className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-muted"
            >
              {group.fiscal ? (
                <Avatar name={group.name} size={22} />
              ) : (
                <span className="grid size-[22px] shrink-0 place-items-center rounded-full bg-warning-bg">
                  <UserX size={12} strokeWidth={1.75} className="text-warning" aria-hidden />
                </span>
              )}
              <span className="text-[13px] font-medium">{group.name}</span>

              {/*
                No celular os quadros descem para a linha de baixo e a seta
                fica junto do nome; de `sm` para cima, a seta fecha a linha.
              */}
              <ChevronDown
                {...ICON}
                size={16}
                className={cn(
                  'ml-auto shrink-0 text-muted-foreground transition-transform duration-100 sm:order-last sm:ml-0',
                  aberto && 'rotate-180',
                )}
                aria-hidden
              />

              <span className="flex basis-full flex-wrap items-center gap-1.5 sm:ml-auto sm:basis-auto">
                <GroupTiles group={group} />
              </span>
            </button>

            {aberto ? (
              <div id={painelId} className="pb-1">
                {group.rows.length === 0 ? (
                  <p className="flex items-center gap-1.5 py-1.5 pl-[42px] text-[12px] text-muted-foreground">
                    <CircleCheck
                      size={13}
                      strokeWidth={1.75}
                      className="text-success"
                      aria-hidden
                    />
                    Nenhuma pendência.
                  </p>
                ) : (
                  <ul>
                    {group.rows.map((row) => (
                      <ObraRow key={row.id} row={row} />
                    ))}
                  </ul>
                )}

                {/* A lista daqui é só o que pede ação; a carteira inteira fica na tela de obras. */}
                <div className="pt-2 pb-1 pl-[42px]">
                  <CardLink
                    to="/obras"
                    search={
                      group.fiscal
                        ? obrasSearch({ fiscalIds: [group.fiscal.id] })
                        : obrasSearch({ pendencias: ['sem_fiscal'] })
                    }
                  >
                    {group.fiscal ? 'Ver as obras do fiscal' : 'Ver as obras sem fiscal'}
                  </CardLink>
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

// ─────────────────────────────────────────────────────────── situação

/*
 * A barra é neutra; o estado vai no ícone e no rótulo. Em andamento e
 * cancelada ficam em cinza porque uma é o esperado e a outra não pede nada.
 * É a mesma escolha da faixa de resumo da listagem de obras.
 */
const STATUS_STYLE: Record<ObraStatus, { icon: LucideIcon; tone: string }> = {
  em_andamento: { icon: CircleDot, tone: 'text-muted-foreground' },
  paralisada: { icon: PauseCircle, tone: 'text-paused' },
  concluida: { icon: CircleCheck, tone: 'text-success' },
  cancelada: { icon: CircleSlash, tone: 'text-muted-foreground' },
};

type LiveVisitStatus = Exclude<VisitStatus, 'nao_se_aplica'>;

const VISIT_STYLE: Record<LiveVisitStatus, { icon: LucideIcon; tone: string }> = {
  em_dia: { icon: CircleCheck, tone: 'text-success' },
  vencendo: { icon: Clock, tone: 'text-warning' },
  vencida: { icon: TriangleAlert, tone: 'text-danger' },
  sem_visita: { icon: CalendarOff, tone: 'text-muted-foreground' },
};

/** Só o que já venceu e o que vence no mês ganham cor; o resto é horizonte. */
const EXPIRATION_STYLE: Record<PainelExpirationBand, { icon: LucideIcon; tone: string }> = {
  vencido: { icon: CalendarClock, tone: 'text-danger' },
  ate_30: { icon: Clock, tone: 'text-warning' },
  ate_60: { icon: CalendarClock, tone: 'text-muted-foreground' },
  ate_90: { icon: CalendarClock, tone: 'text-muted-foreground' },
  depois_de_90: { icon: CalendarClock, tone: 'text-muted-foreground' },
  sem_prazo: { icon: CalendarOff, tone: 'text-muted-foreground' },
};

/**
 * Quatro leituras do mesmo acervo, lado a lado e no mesmo formato: situação
 * das obras, visitas nas obras em curso, vencimento dos contratos em curso e
 * o que cada secretaria demandou. Uma lista por coluna, com o número escrito —
 * nada a decifrar.
 */
export function StatusOverview({
  totals,
  visits,
  expirations,
  secretarias,
  fiscalId,
}: {
  totals: PainelTotals;
  visits: Readonly<Record<VisitStatus, number>>;
  expirations: readonly PainelExpiration[];
  secretarias: readonly PainelSecretariaRow[];
  /** A carteira, no painel do fiscal: os atalhos abrem a lista com ela. */
  fiscalId: string | null;
}) {
  const statuses = (Object.keys(STATUS_STYLE) as ObraStatus[]).filter(
    (status) => status !== 'cancelada' || totals.byStatus[status] > 0,
  );
  /*
   * Cada linha que a lista de obras sabe recortar vira atalho para ela, com a
   * carteira do fiscal quando é o painel dele. "Em dia" e as faixas de
   * vencimento ainda por vir não têm filtro correspondente e ficam sem link.
   */
  const carteira = fiscalId ? { fiscalIds: [fiscalId] } : {};
  const linkTo = (recorte: Parameters<typeof obrasSearch>[0]): ObrasLink => ({
    to: '/obras',
    search: obrasSearch({ ...recorte, ...carteira }),
  });

  const statusRows: BarRow[] = statuses.map((status) => ({
    key: status,
    label: OBRA_STATUS_LABELS[status],
    icon: STATUS_STYLE[status].icon,
    iconTone: STATUS_STYLE[status].tone,
    value: totals.byStatus[status],
    display: String(totals.byStatus[status]),
    detail: formatPercent(ratio(totals.byStatus[status], totals.obras), 0),
    link: linkTo({ statuses: [status] }),
  }));

  const visitKeys = Object.keys(VISIT_STYLE) as LiveVisitStatus[];
  const aliveCount = visitKeys.reduce((soma, key) => soma + visits[key], 0);
  const visitRows: BarRow[] = visitKeys.map((key) => ({
    key,
    label: VISIT_STATUS_LABELS[key],
    icon: VISIT_STYLE[key].icon,
    iconTone: visits[key] > 0 ? VISIT_STYLE[key].tone : 'text-muted-foreground',
    value: visits[key],
    display: String(visits[key]),
    detail: formatPercent(ratio(visits[key], aliveCount), 0),
    link:
      key === 'vencida'
        ? linkTo({ pendencias: ['visita_vencida'] })
        : key === 'vencendo'
          ? linkTo({ pendencias: ['visita_vencendo'] })
          : key === 'sem_visita'
            ? linkTo({ pendencias: ['sem_visita'] })
            : undefined,
  }));

  const expirationRows: BarRow[] = expirations.map((row) => ({
    key: row.band,
    label: PAINEL_EXPIRATION_LABELS[row.band],
    icon: EXPIRATION_STYLE[row.band].icon,
    iconTone: row.obras > 0 ? EXPIRATION_STYLE[row.band].tone : 'text-muted-foreground',
    value: row.obras,
    display: String(row.obras),
    detail: row.obras > 0 ? `${formatCurrencyCompact(row.remainingCents)} a pagar` : undefined,
    link: row.band === 'vencido' ? linkTo({ pendencias: ['contrato_vencido'] }) : undefined,
  }));

  /* Claro é o contratado, escuro é o pago: a mesma leitura do cartão de obras. */
  const secretariaRows: BarRow[] = secretarias.map((row) => {
    const execucao = ratio(row.paidCents, row.contractedCents);

    return {
      key: row.id ?? 'sem-secretaria',
      label: row.acronym ?? row.name,
      sublabel: row.acronym ? row.name : undefined,
      value: row.contractedCents,
      fill: execucao,
      display: formatCurrencyCompact(row.contractedCents),
      detail: `${formatPercent(execucao, 0)} pago · ${plural(row.obras, 'obra', 'obras')}`,
    };
  });

  const semObraEmCurso = (
    <p className="text-[13px] text-muted-foreground">Nenhuma obra em curso.</p>
  );

  return (
    <div className="grid gap-x-10 gap-y-7 px-5 pb-5 md:grid-cols-2">
      <section>
        <SectionLabel>Situação das obras</SectionLabel>
        <BarList rows={statusRows} max={totals.obras} labelWidth="sm" />
      </section>

      <section>
        <SectionLabel>Visitas nas obras em curso</SectionLabel>
        {aliveCount === 0 ? (
          semObraEmCurso
        ) : (
          <BarList rows={visitRows} max={aliveCount} labelWidth="sm" />
        )}
      </section>

      <section>
        <SectionLabel>Vencimento dos contratos em curso</SectionLabel>
        {aliveCount === 0 ? (
          semObraEmCurso
        ) : (
          <BarList rows={expirationRows} max={aliveCount} labelWidth="sm" />
        )}
      </section>

      <section>
        <SectionLabel>Por secretaria demandante</SectionLabel>
        {secretariaRows.length === 0 ? (
          semObraEmCurso
        ) : (
          <BarList rows={secretariaRows} labelWidth="sm" />
        )}
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────── medições por mês

export function MonthlyMeasured({
  months,
  today,
}: {
  months: readonly PainelMonth[];
  today: string;
}) {
  const atual = today.slice(0, 7);
  const total = months.reduce((soma, mes) => soma + mes.measuredCents, 0);
  const relatorios = months.reduce((soma, mes) => soma + mes.reports, 0);

  const pico = months.reduce<PainelMonth | null>(
    (maior, mes) => (mes.measuredCents > (maior?.measuredCents ?? 0) ? mes : maior),
    null,
  );

  const items: ColumnItem[] = months.map((mes) => {
    const emCurso = mes.month === atual;
    const rotulo = rotuloMes(mes.month);

    return {
      key: mes.month,
      label: rotulo,
      value: mes.measuredCents,
      tone: emCurso ? 'soft' : 'neutral',
      tip: {
        title: emCurso ? `${rotulo} · em curso` : rotulo,
        rows: [
          { label: 'Medido', value: formatCurrency(mes.measuredCents) },
          { label: 'Relatórios', value: String(mes.reports) },
          { label: 'Visitas', value: String(mes.visits) },
        ],
      },
      aria: `${rotulo}: ${formatCurrency(mes.measuredCents)} medidos, ${plural(mes.reports, 'relatório', 'relatórios')}`,
    };
  });

  const summary =
    total > 0 ? (
      <>
        {formatCurrency(total)} em doze meses · maior mês:{' '}
        {pico ? `${rotuloMes(pico.month)}, ${formatCurrencyCompact(pico.measuredCents)}` : '—'}
      </>
    ) : (
      <>{plural(relatorios, 'relatório publicado', 'relatórios publicados')} em doze meses</>
    );

  return (
    <ChartFrame
      summary={summary}
      table={
        <ChartTable
          head={['Mês', 'Medido', 'Relatórios', 'Visitas']}
          rows={months.map((mes) => [
            rotuloMes(mes.month),
            formatCurrency(mes.measuredCents),
            String(mes.reports),
            String(mes.visits),
          ])}
        />
      }
    >
      {total > 0 ? (
        <Columns items={items} format={formatCurrencyCompact} height={200} />
      ) : (
        <Quiet icon={TrendingUp}>Nenhuma medição nos últimos doze meses.</Quiet>
      )}
    </ChartFrame>
  );
}

// ─────────────────────────────────────────────────────── atividade recente

/** Ícones canônicos das movimentações — design.md §7. */
const TYPE_ICON: Record<string, LucideIcon> = {
  ClipboardCheck,
  TrendingUp,
  Plus,
  Minus,
  CalendarPlus,
  CircleAlert,
};

/** A cor da movimentação é lei: a mesma na timeline, no badge e aqui — design.md §4. */
const MARKER_TONE: Record<string, string> = {
  muted: 'bg-muted-foreground',
  success: 'bg-success',
  info: 'bg-info',
  danger: 'bg-danger',
  warning: 'bg-warning',
};

const DELTA_TONE: Record<string, string> = {
  muted: 'text-muted-foreground',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

/** O que a movimentação fez com o contrato, em uma palavra numérica. */
function deltaOf(item: PainelActivity): string | null {
  const meta = REPORT_TYPE_META[item.type];

  if (item.days !== null && (meta.effect === 'contract_term' || meta.effect === 'os_term')) {
    return `+${formatDays(item.days)}`;
  }
  if (item.amountCents === null || meta.effect === 'none') return null;
  if (meta.effect === 'gloss') return formatCurrency(item.amountCents);

  return formatCurrencyDelta(item.amountCents);
}

export function ActivityList({ rows }: { rows: readonly PainelActivity[] }) {
  if (rows.length === 0) {
    return <Quiet icon={FileText}>Nenhum relatório publicado até agora.</Quiet>;
  }

  return (
    <ul className="grid px-2 pb-2 lg:grid-cols-2 lg:gap-x-4">
      {rows.map((item) => {
        const meta = REPORT_TYPE_META[item.type];
        const Icon = TYPE_ICON[meta.icon] ?? FileText;
        const delta = deltaOf(item);

        return (
          <li key={item.id}>
            <Link
              to="/obras/$obraId"
              params={{ obraId: item.obraId }}
              className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-muted"
            >
              <span
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-full',
                  MARKER_TONE[meta.tone] ?? 'bg-muted-foreground',
                )}
              >
                <Icon {...ICON} size={14} className="text-white" aria-hidden />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium">{item.title}</span>
                <span className="block truncate text-[12px] text-muted-foreground">
                  {item.obraName} · {item.authorName ?? 'autor removido'} ·{' '}
                  {formatRelativeDate(item.visitDate)}
                </span>
              </span>

              {delta ? (
                <span
                  className={cn(
                    'tabular shrink-0 text-[13px] font-medium',
                    item.reversed ? 'text-muted-foreground line-through' : DELTA_TONE[meta.tone],
                  )}
                  title={item.reversed ? `${meta.label} · estornada` : meta.label}
                >
                  {delta}
                </span>
              ) : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Estado vazio de página inteira: não há obra para ler. */
export function NoObras({ scope }: { scope: 'acervo' | 'carteira' }) {
  return (
    <EmptyState
      icon={HardHat}
      title={scope === 'carteira' ? 'Nenhuma obra sob sua fiscalização' : 'Nenhuma obra cadastrada'}
      description={
        scope === 'carteira'
          ? 'O painel passa a mostrar prazos, visitas e medições quando uma obra for designada a você.'
          : 'Cadastre a primeira obra para o painel começar a acompanhar prazos, visitas e medições.'
      }
      action={<CardLink to="/obras">Ir para obras</CardLink>}
    />
  );
}
